/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChild, ComponentProps, Fragment } from 'preact';
import { useCallback } from 'preact/hooks';
import { TransitionGroup } from '../PRIVATE_TransitionGroup';
import { Item } from '../utils/UNSAFE_dataProvider';
import { MessageTransition } from './MessageTransition';

/**
 * Metadata passed on to the Transition component
 */
type TransitionMetadata<K> = {
  index: number;
  key: K;
};

/**
 * Type of the render context passed on to render individual message
 */
type MessageRenderContext<Key, Data> = {
  /**
   * The index of the current message
   */
  index: number;

  /**
   * The message item with all the necessary data in it
   */
  item: Item<Key, Data>;
};

type PickedPropsFromMessageTransition = Pick<
  ComponentProps<typeof MessageTransition>,
  'animationStates' | 'initialAnimationStyles'
>;

/**
 * Props for the Message Component
 */
type Props<Key, Data> = PickedPropsFromMessageTransition & {
  /**
   * A render function that renders individual child.
   */
  children?: (context: MessageRenderContext<Key, Data>) => ComponentChild;

  /**
   * Data for the messages. This data is used for rendering each message.
   */
  data: Item<Key, Data>[];

  /**
   * A callback function that gets called before a message is removed
   */
  onMessageWillRemove?: (key: Key, index: number, node?: Element) => void;
};

/**
 * The component that renders individual messages for the provided data.
 */
export function MessagesManager<K extends string | number = string | number, D = any>({
  data,
  animationStates,
  children,
  initialAnimationStyles,
  onMessageWillRemove
}: Props<K, D>) {
  /**
   * Handles when a message has finished to exit.
   *
   * @param node The corresponding message element
   * @param callback A callback function to be called after the animation is complete
   */
  const handleExited = useCallback(
    async (node?: Element, metadata?: TransitionMetadata<K>) => {
      metadata && onMessageWillRemove?.(metadata.key, metadata.index, node);
    },
    [onMessageWillRemove]
  );

  return (
    <TransitionGroup elementType={Fragment}>
      {data.map((item, index) => (
        <MessageTransition
          animationStates={animationStates}
          initialAnimationStyles={initialAnimationStyles}
          key={item.key}
          metadata={{ index, key: item.key }}
          onExited={handleExited}>
          {children?.({ index, item })}
        </MessageTransition>
      ))}
    </TransitionGroup>
  );
}
